import {type ChartAggOption} from '../../../../generated/graphql';
import {BIN_POINT_TO_USE} from '../../../../util/plotHelpers/chartTypes';
import {type PointWithMeta} from '../../componentOutliers/buildPoints';
import {
  type HistoryColumns,
  type LineAggregation,
  type ParseHistoryConfig,
} from '../../componentOutliers/types';
import {mapGroupAggToSuffix} from '../../componentOutliers/util';

type Reader = (row: number) => number;

const readNaN: Reader = () => NaN;
const readFalse = (): boolean => false;

function columnReader(chunk: HistoryColumns, key: string): Reader | undefined {
  const direct = chunk.cols[key];
  if (direct !== undefined) {
    return row => direct[row];
  }
  return undefined;
}

// XAt readers return raw, unshifted positions; appendAvgPoints applies xAtShift.
type AvgSeries = {
  length: number;
  readX: Reader;
  readY: Reader;
  readYMin: Reader;
  readYMax: Reader;
  readXAtMin: Reader;
  readXAtMax: Reader;
  hasNaN: (row: number) => boolean;
  extend?: (point: PointWithMeta, row: number) => void;
};

type BandSeries = {
  length: number;
  readX: Reader;
  readMin: Reader;
  readMax: Reader;
  readAvg: Reader;
  readStdDev: Reader;
  readStdErr: Reader;
  bucketSize: Reader;
  extend?: (point: PointWithMeta, row: number) => void;
};

// Primary-line points for one bucket group. In min-max-last mode a bucket with
// finite XAtMin/XAtMax expands into its low/high/last anchors; otherwise one point.
function appendAvgPoints(
  series: AvgSeries,
  opts: {tryExpand: boolean; xAtShift: number},
  finitePoints: PointWithMeta[],
  nanPoints: PointWithMeta[]
): void {
  const {
    length,
    readX,
    readY,
    readYMin,
    readYMax,
    readXAtMin,
    readXAtMax,
    hasNaN,
    extend,
  } = series;
  for (let row = 0; row < length; row++) {
    const lastX = readX(row);
    if (!isFinite(lastX)) {
      continue;
    }
    const lastY = readY(row);

    if (opts.tryExpand) {
      // Single-point buckets carry NaN XAtMin/XAtMax and fall through below.
      const xAtMinRaw = readXAtMin(row);
      const xAtMaxRaw = readXAtMax(row);
      if (isFinite(xAtMinRaw) && isFinite(xAtMaxRaw)) {
        const xAtMin = xAtMinRaw - opts.xAtShift;
        const xAtMax = xAtMaxRaw - opts.xAtShift;
        // Min/Max can be ±Inf even when XAt is finite; route those to nan.
        const yMin = readYMin(row);
        const yMax = readYMax(row);
        const yMinFinite = isFinite(yMin);
        const yMaxFinite = isFinite(yMax);
        // Emit the two anchors in x order; xLast is always rightmost.
        if (xAtMin <= xAtMax) {
          (yMinFinite ? finitePoints : nanPoints).push({x: xAtMin, y: yMin});
          (yMaxFinite ? finitePoints : nanPoints).push({x: xAtMax, y: yMax});
        } else {
          (yMaxFinite ? finitePoints : nanPoints).push({x: xAtMax, y: yMax});
          (yMinFinite ? finitePoints : nanPoints).push({x: xAtMin, y: yMin});
        }
        const lastYFinite = isFinite(lastY);
        (lastYFinite ? finitePoints : nanPoints).push({x: lastX, y: lastY});
        if (lastYFinite && hasNaN(row)) {
          nanPoints.push({x: lastX, y: NaN});
        }
        continue;
      }
    }

    const point: PointWithMeta = {x: lastX, y: lastY};
    extend?.(point, row);
    if (isFinite(point.y)) {
      finitePoints.push(point);
      if (hasNaN(row)) {
        nanPoints.push({...point, y: NaN});
      }
    } else {
      nanPoints.push(point);
    }
  }
}

// Band points for one bucket group; single-point buckets with a non-finite bound
// are skipped since the primary line covers them.
function appendBandPoints(
  series: BandSeries,
  agg: LineAggregation,
  finitePoints: PointWithMeta[]
): void {
  const {
    length,
    readX,
    readMin,
    readMax,
    readAvg,
    readStdDev,
    readStdErr,
    bucketSize,
    extend,
  } = series;
  for (let row = 0; row < length; row++) {
    const x = readX(row);
    let v: PointWithMeta;
    if (agg === 'stdDev') {
      const avg = readAvg(row);
      const stdDev = readStdDev(row);
      v = {x, y: avg - stdDev, y0: avg + stdDev};
    } else if (agg === 'stdErr') {
      const avg = readAvg(row);
      const stdErr = readStdErr(row);
      v = {x, y: avg - stdErr, y0: avg + stdErr};
    } else {
      v = {x, y: readMin(row), y0: readMax(row)};
    }
    extend?.(v, row);

    if (
      bucketSize(row) === 1 &&
      (!isFinite(v.y) || !isFinite(v.y0 as number))
    ) {
      continue;
    }

    const hasANaNpoint = isNaN(v.y) || isNaN(v.y0 as number);
    const hasAFinitePoint = isFinite(v.y) || isFinite(v.y0 as number);
    const pointsAreEqual = v.y === v.y0;
    if (!hasANaNpoint && (hasAFinitePoint || !pointsAreEqual)) {
      finitePoints.push(v);
    }
  }
}

function columnarExtend(
  chunk: HistoryColumns,
  xMetrics: string[],
  ySuffix: string
): (point: PointWithMeta, row: number) => void {
  const readers: Array<{metric: string; read: Reader}> = [];
  for (const m of xMetrics) {
    const read =
      columnReader(chunk, `${m}${ySuffix}`) ?? columnReader(chunk, m);
    if (read !== undefined) {
      readers.push({metric: m, read});
    }
  }
  return (point, row) => {
    if (!point.expressionValues) {
      point.expressionValues = {};
    }
    for (const r of readers) {
      point.expressionValues[r.metric] = r.read(row);
    }
  };
}

// Columnar twin of parseHistory: builds the same points from typed-array
// columns.
export function parseHistoryColumnar(
  cols: HistoryColumns[],
  metric: string,
  xMetric: string,
  otherMetrics: string[],
  config: ParseHistoryConfig,
  groupAgg?: ChartAggOption
): {finitePoints: PointWithMeta[]; nanPoints: PointWithMeta[]} {
  const finitePoints: PointWithMeta[] = [];
  const nanPoints: PointWithMeta[] = [];

  const xMetrics = otherMetrics.concat(xMetric);
  const ySuffix = groupAgg ? mapGroupAggToSuffix[groupAgg] : BIN_POINT_TO_USE;
  const tryExpand =
    !config.usingExpressions && config.primaryLineType === 'min-max-last';

  for (const chunk of cols) {
    if (chunk.numRows < 1) {
      continue;
    }
    const readX =
      columnReader(chunk, `${xMetric}Last`) ??
      columnReader(chunk, xMetric) ??
      readNaN;
    const extend = config.usingExpressions
      ? columnarExtend(chunk, xMetrics, ySuffix)
      : undefined;

    if (config.agg === 'Avg') {
      const hasNaNCol = chunk.cols[`${metric}HasNaN`];
      appendAvgPoints(
        {
          length: chunk.numRows,
          readX,
          readY:
            columnReader(chunk, `${metric}${ySuffix}`) ??
            columnReader(chunk, metric) ??
            readNaN,
          readYMin: columnReader(chunk, `${metric}Min`) ?? readNaN,
          readYMax: columnReader(chunk, `${metric}Max`) ?? readNaN,
          readXAtMin: columnReader(chunk, `${metric}XAtMin`) ?? readNaN,
          readXAtMax: columnReader(chunk, `${metric}XAtMax`) ?? readNaN,
          hasNaN:
            hasNaNCol !== undefined
              ? row => Boolean(hasNaNCol[row])
              : readFalse,
          extend,
        },
        {tryExpand, xAtShift: 0},
        finitePoints,
        nanPoints
      );
    } else {
      const bucketSizeCol = chunk.cols.bucketSize;
      appendBandPoints(
        {
          length: chunk.numRows,
          readX,
          readMin: columnReader(chunk, `${metric}Min`) ?? readNaN,
          readMax: columnReader(chunk, `${metric}Max`) ?? readNaN,
          readAvg: columnReader(chunk, `${metric}Avg`) ?? readNaN,
          readStdDev: columnReader(chunk, `${metric}StdDev`) ?? readNaN,
          readStdErr: columnReader(chunk, `${metric}StdErr`) ?? readNaN,
          bucketSize:
            bucketSizeCol !== undefined ? row => bucketSizeCol[row] : readNaN,
          extend,
        },
        config.agg,
        finitePoints
      );
    }
  }

  // No sort/dedup: the dyadic-arrow wire format emits buckets with strictly
  // ascending, non-duplicate x (lattice addrs are strictly ascending and x is
  // enforced monotonic), and min-max-last anchors stay ordered within their
  // non-overlapping bucket cell. See services/gorilla/docs/line-plots/sketch-wire-format.md.
  return {finitePoints, nanPoints};
}
